import React from 'react'
import type { Dispatch, SetStateAction } from 'react'
import allAlerts from '@/config/alerts.json'
import { Fragment } from 'react'
import { AnimatePresence, motion, useAnimation } from 'framer-motion'
import { useQuery } from 'react-query'
import language_translations, { LanguageTranslations } from '@/config/language'
import { useRouter } from 'next/router'
import {
  GetDocument,
  GetNotebookDocument,
} from '@/services/api/personalize/GetDocuments'
import { GetBySlugQuery } from '@/services/api/products/GetBySlug'
import Book from '@/components/Personalize/AIBook'
import { useCartDispatch } from '@/contexts/CartContext'
import 'intro.js/introjs.css'
import 'intro.js/introjs-rtl.css'
import PersonalizeLoader from '@/components/Personalize/PersonalizeLoader'
import BookMobile from './AIBookMobile'
import toast from 'react-hot-toast'
import CustomToast from '../CustomToast'
import Button from '../Button'
import { Dialog, Transition } from '@headlessui/react'
import { addItem, updateItem } from 'CartActions'
import useUserPreferences from '@/hooks/useUserPreferences'
import { useCurrency } from '../DesktopHeader'

interface Props {
  isOpen: boolean
  setIsNotebookOpen: Dispatch<SetStateAction<boolean>>
  slug: string
  inputName: string

  uuid: string
  qty: number
  setQty: Dispatch<SetStateAction<any>>
  setInputName: Dispatch<SetStateAction<string>>
  inputLanguage: keyof LanguageTranslations | undefined

  setInputLanguage: Dispatch<
    SetStateAction<keyof LanguageTranslations | undefined>
  >
  isEdit?: boolean
}
export default function NotebookDetailsModal({
  isOpen,
  setIsNotebookOpen,
  slug,
  inputName,
  setInputName,
  inputLanguage,
  setInputLanguage,
  qty,
  setQty,
  isEdit = false,
  uuid
}: Props) {
  const router = useRouter()
  const {locale} = useRouter();
  const preferences = useUserPreferences();
  const currencySymbol = useCurrency(preferences?.currencyCode || "ILS");
  const alerts = allAlerts[locale as keyof typeof allAlerts] || allAlerts?.ar;
  const { data: product, isLoading: isProductLoading } = useQuery(
    ['product', slug],
    GetBySlugQuery,
  )
  const { data, isLoading } = useQuery(
    [
      'personalize',
      {
        slug: slug,
        name: inputName,
        language: inputLanguage,
      },
    ],
    GetNotebookDocument,
  )

  const cartDispatch = useCartDispatch()
  // const demoName = locale === 'ar' ? product.demo_name?.ar : product.demo_name?.en;
  // const Description = locale === 'ar' ? product.description?.ar : product.description?.en;
  // const Name = locale === 'ar' ? product.product_name?.ar : product.product_name?.en;

  const addToCart = () => {
    //Validation
    const total_price = parseFloat(product.front_price) * qty

    const item = {
      product_id: product.id,
      replace_name: inputName,
      inputs: { name: inputName },
      product_name: product.product_name,
      demo_name: product.demo_name,
      cover_id: null,
      default_replace_name: locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
      image: product.images[0],
      product_slug: product.slug,
      price: parseFloat(product.front_price),
      quantity: qty,
      total: total_price,
      language: inputLanguage,
      product_type: 2 as 2,
      discount: 0,
      uuid: uuid,
    }

    if (isEdit) {
      //cartDispatch({ type: 'UPDATE_CART', payload: item })
      cartDispatch(updateItem(item))
      toast.custom((t) => <CustomToast t={t} message={alerts.cart_updated} type='success' />);
    } else {
      //cartDispatch({ type: 'ADD_CART', payload: item })
      cartDispatch(addItem(item))
      toast.custom((t) => <CustomToast t={t} message={alerts.cart_added} type='success' />);
    }

    //router.push('/cart')
  }
  if (product) {
    return (
      <>
        <Transition appear show={isOpen} as={Fragment}>
          <Dialog as="div" className="relative z-[110]" onClose={() => { }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center lg:p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full md:max-w-md lg:max-w-[90%] transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                    <Dialog.Title className="text-lg font-medium p-3 lg:p-6 lg:pb-0">
                      <div className="flex  items-end justify-between">
                        <h3 className="text-primary-500 text-right font-bold text-3xl"></h3>
                        <a
                          href="#"
                          onClick={e => {
                            e.preventDefault()
                            setIsNotebookOpen(false)
                          }}
                          className="close-icon border bg-gray-200 rounded-md p-2 self-end cursor-pointer">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 21 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M11.9109 10.2326L20.201 1.94261C20.3648 1.75131 20.4504 1.50523 20.4407 1.25355C20.4309 1.00188 20.3266 0.763139 20.1485 0.585044C19.9704 0.40695 19.7317 0.302617 19.48 0.292896C19.2283 0.283175 18.9823 0.368781 18.791 0.532608L10.5009 8.82261L2.21095 0.522607C2.02265 0.334304 1.76725 0.228516 1.50095 0.228516C1.23465 0.228516 0.979253 0.334304 0.79095 0.522607C0.602646 0.710911 0.496858 0.966306 0.496858 1.23261C0.496858 1.49891 0.602646 1.7543 0.79095 1.94261L9.09095 10.2326L0.79095 18.5226C0.686268 18.6123 0.601248 18.7226 0.541224 18.8466C0.481201 18.9707 0.447471 19.1058 0.442151 19.2436C0.436832 19.3813 0.460037 19.5186 0.510312 19.6469C0.560587 19.7753 0.636845 19.8918 0.7343 19.9893C0.831754 20.0867 0.948302 20.163 1.07663 20.2132C1.20495 20.2635 1.34228 20.2867 1.48 20.2814C1.61772 20.2761 1.75285 20.2424 1.87692 20.1823C2.00098 20.1223 2.1113 20.0373 2.20095 19.9326L10.5009 11.6426L18.791 19.9326C18.9823 20.0964 19.2283 20.182 19.48 20.1723C19.7317 20.1626 19.9704 20.0583 20.1485 19.8802C20.3266 19.7021 20.4309 19.4633 20.4407 19.2117C20.4504 18.96 20.3648 18.7139 20.201 18.5226L11.9109 10.2326Z"
                              fill="gray"
                            />
                          </svg>
                        </a>
                      </div>
                    </Dialog.Title>
                    <div className="modal-content p-3 pt-0 lg:p-4 lg:pt-0">
                      {isProductLoading && isLoading ? (
                        <AnimatePresence>
                          <motion.div
                            initial={{ opacity: 0, translateY: '60%' }}
                            transition={{ type: 'spring', duration: 0.5 }}
                            animate={{ opacity: 1, translateY: '0' }}
                            exit={{ opacity: 0, translateY: '60%' }}
                            className="personalize-loader flex flex-col justify-center items-center">
                            <PersonalizeLoader />
                          </motion.div>
                        </AnimatePresence>
                      ) : (
                        <main>
                          <div className="mt-4 notebook-preview-grid lg:grid px-2 lg:px-4 pb-12">
                            <div className="product-preview">
                              <div className="headers">
                                <div className="flex flex-col items-center">
                                  {inputLanguage && (
                                    <h1 className="text-center prd-name text-2xl font-semibold text-primary">
                                      تصفح دفتر شخصي
                                      {' ' +
                                        language_translations[inputLanguage] +
                                        ' '}
                                      <span className="text-success mr-2">
                                        {'ل' +
                                          (inputName ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en) +
                                          ' '}
                                      </span>
                                    </h1>
                                  )}
                                </div>
                              </div>

                              <div className="mobile-book-container lg:hidden mx-auto overflow-hidden mt-6">
                                {!isLoading && (
                                  <BookMobile
                                    pages={data?.pages.pdf}
                                    cover_pages={data?.cover_pages.pdf}
                                    cover_pages_predefined_texts={
                                      data?.cover_pages_predefined_texts
                                    }
                                    inputDedication={''}
                                    product={product}
                                    pages_predefined_texts={
                                      data?.pages_predefined_texts
                                    }
                                    pages_dedication_texts={
                                      data?.pages_dedication_texts
                                    }
                                    bookView='single'
                                  />
                                )}
                              </div>

                              <div className="hidden lg:block personalize-img overflow-hidden mt-6">
                                {!isLoading && (
                                  <div className="shadow-lg">
                                    <Book
                                      pages={data?.pages.pdf}
                                      cover_pages={data?.cover_pages.pdf}
                                      cover_pages_predefined_texts={
                                        data?.cover_pages_predefined_texts
                                      }
                                      inputDedication={''}
                                      product={product}
                                      width={325}
                                      minWidth={325}
                                      maxWidth={325}
                                      height={450}
                                      minHeight={450}
                                      maxHeight={450}
                                      pages_predefined_texts={
                                        data?.pages_predefined_texts
                                      }
                                      pages_dedication_texts={
                                        data?.pages_dedication_texts
                                      }
                                      bookView='single'
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="product-info text-right">
                              <h1 className="product-title lg:block mt-6 text-2xl font-bold text-primary">
                                {product?.demo_name && typeof product.demo_name === 'string'
                                  ? product.demo_name.replace(
                                      '{basmti}',
                                      inputName ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en ?? ''
                                    )
                                  : product?.demo_name || ''
                                }
                                <span className="text-xs font-light mr-2">
                                  (4 دفاتر)
                                </span>
                              </h1>
                              <div
                                className="mt-2 leading-normal font-light"
                                dangerouslySetInnerHTML={{
                                  __html: product?.description && typeof product.desccription === 'string' ? product?.description?.replace(
                                    new RegExp('{basmti}', 'g'),
                                    inputName ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
                                  ) : locale === 'ar' ? product?.description?.ar.replace(
                                    new RegExp('{basmti}', 'g'),
                                    inputName ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
                                  ) : product?.description?.en.replace(
                                    new RegExp('{basmti}', 'g'),
                                    inputName ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
                                  ) || '',
                                }}></div>
                              <div className="product-languages mt-5 flex items-center">
                                <strong className="text-primary font-medium">
                                  نوع الرزمة:
                                </strong>

                                <div className="languages flex flex-wrap gap-2 mr-5">
                                  {product.languages.map(
                                    (
                                      language: keyof LanguageTranslations,
                                      index: number,
                                    ) => {
                                      return (
                                        <a
                                          href="#"
                                          key={index}
                                          onClick={e => {
                                            e.preventDefault()
                                            if (language != undefined) {
                                              setInputLanguage(language)
                                            }
                                          }}
                                          className={`bg-success ${inputLanguage == language
                                            ? 'opacity-100'
                                            : 'opacity-50'
                                            } px-4 py-2 leading-[1] rounded-md font-medium transition-all duration-300`}>
                                          <b className="font-medium text-primary ">
                                            {language_translations[language]}
                                          </b>
                                        </a>
                                      )
                                    },
                                  )}
                                </div>
                              </div>

                              <div className="w-full form-group mt-10">
                                <label className="block text-xs text-primary">
                                  الإسم الكامل للطفل
                                </label>
                                <div className="name-input flex items-center mt-2">
                                  <input
                                    type={'text'}
                                    name={'name'}
                                    placeholder="ادخل اسم الطفل/ة"
                                    onChange={e => {
                                      setInputName(e.target.value)
                                    }}
                                    value={inputName}
                                    className="border-1 text-primary border-gray-300 w-full font-semibold placeholder:font-light placeholder:opacity-70 py-3 text-xs px-3 focus:ring-1 transition duration-200 focus:ring-black focus:border-transparent
                                rounded-md "
                                  />
                                  <a
                                    href="#"
                                    className="flex items-center justify-center border border-primary-500 py-2 rounded-md px-2 mr-2 text-sm"
                                    onClick={e => {
                                      e.preventDefault()

                                      if (
                                        inputName == '' ||
                                        inputLanguage == undefined
                                      ) {
                                        toast.custom((t) => <CustomToast t={t} message={'Please Enter a name & Language first'} type='error' />);
                                      }
                                    }}>
                                    <span className="text-primary">
                                      استعراض
                                    </span>
                                  </a>
                                </div>
                              </div>
                              <div className="w-full mt-9">
                                <div className="flex flex-col">
                                  <div className="flex items-center">
                                    <p className="text-primary">
                                      عدد الرزم{' '}
                                      <span className="font-light text-sm">
                                        ( الرزمة تحتوي على 4 دفاتر ):
                                      </span>
                                    </p>
                                    <div className="quantity flex items-center mr-6">
                                      <button
                                        className="bg-success h-10 w-10 text-2xl rounded-md text-primary"
                                        onClick={e => {
                                          e.preventDefault()
                                          setQty((prev: any) =>
                                            prev < 6 ? prev + 1 : prev,
                                          )
                                        }}>
                                        +
                                      </button>
                                      <span className="text-primary px-3 text-lg">
                                        {qty}
                                      </span>
                                      <button
                                        className={`bg-success h-10 w-10 text-2xl rounded-md text-primary ${qty === 1
                                          ? 'opacity-50'
                                          : 'opacity-100'
                                          }`}
                                        onClick={e => {
                                          e.preventDefault()
                                          setQty((prev: any) =>
                                            prev > 1 ? prev - 1 : prev,
                                          )
                                        }}>
                                        -
                                      </button>
                                    </div>
                                  </div>
                                  <p className="price flex border-t-4 mt-6 py-2 pt-4">
                                    <span className="text-sm">المبلغ:</span>
                                    <span className="text-red font-semibold text-xl mr-12">
                                      {currencySymbol.symbol}{product.price * qty}
                                    </span>
                                  </p>
                                </div>
                              </div>

                              <Button
                                type="button"
                                bg="primary"
                                classes={['w-full', 'py-3 mt-3']}
                                onClick={e => {
                                  e.preventDefault()
                                  if (
                                    inputName == '' ||
                                    inputLanguage == undefined
                                  ) {
                                    toast.custom((t) => <CustomToast t={t} message={'Please Enter a name & Language first'} type='error' />);
                                  } else {
                                    addToCart()
                                    setIsNotebookOpen(false)
                                  }
                                }}>
                                <span>اضافة للسلة</span>
                              </Button>
                            </div>
                          </div>
                        </main>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </>
    )
  }
  return <></>
}